import React, { useEffect, useMemo, useState } from 'react';
import { Form, Button, Row, Col, Space, Collapse } from 'antd';
import { DynamicFormProps, FieldConfig } from '../types';
import FormField from './FormField';
import { useFieldDependencies } from '../utils/useFieldDependencies';
import { useFormDirty } from '../utils/useFormDirty';
import { useConditionalFields } from '../utils/useConditionalFields';

const { Panel } = Collapse;

/**
 * DynamicForm - Main form component
 */
const DynamicForm: React.FC<DynamicFormProps> = ({
  config,
  mode = 'create',
  initialValues,
  onSubmit,
  onChange,
  onDirtyChange,
  onValidationFail,
  validateOnChange = false,
  className,
  loading = false,
  readonly = false,
}) => {
  const [form] = Form.useForm();
  const [formValues, setFormValues] = useState<Record<string, any>>(
    initialValues || {}
  );

  // Determine if form should be readonly based on mode
  const isReadonly = readonly || mode === 'view';

  // Get all fields (from sections or flat fields)
  const baseFields = useMemo(() => {
    if (config.sections) {
      return config.sections.flatMap((section) => section.fields);
    }
    return config.fields || [];
  }, [config]);

  // Apply conditional fields logic
  const allFields = useConditionalFields(baseFields, formValues);

  // Set up field dependencies
  const { dependentOptions, loadingFields, handleDependencyChange } =
    useFieldDependencies(form, allFields);

  // Set up dirty state tracking
  const { isDirty, checkDirtyState, resetDirtyState } = useFormDirty(
    form,
    initialValues,
    onDirtyChange
  );

  // Initialize form with initial values
  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues);
      setFormValues(initialValues);
    }
  }, [initialValues, form]);

  // Handle form value changes
  const handleValuesChange = (changedValues: any, allValues: any) => {
    setFormValues(allValues);
    checkDirtyState();

    // Handle field dependencies
    Object.keys(changedValues).forEach((fieldName) => {
      const dependentFields = allFields.filter((field) => {
        if (Array.isArray(field.dependsOn)) {
          return field.dependsOn.includes(fieldName);
        }
        return field.dependsOn === fieldName;
      });

      dependentFields.forEach((field) => {
        handleDependencyChange(field, fieldName, changedValues[fieldName]);
      });
    });

    // Validate on change if enabled
    if (validateOnChange) {
      form.validateFields();
    }

    // Call onChange callback
    onChange?.(allValues, changedValues);
  };

  // Handle form submission
  const handleFinish = async (values: Record<string, any>) => {
    try {
      await onSubmit?.(values);
      resetDirtyState();
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  // Handle validation failure
  const handleFinishFailed = (errorInfo: any) => {
    onValidationFail?.(errorInfo);
  };

  // Merge field options with dependent options
  const getFieldOptions = (field: FieldConfig) => {
    if (dependentOptions[field.name]) {
      return dependentOptions[field.name];
    }
    return field.options || [];
  };

  // Render a single field
  const renderField = (field: FieldConfig, span?: number | object) => {
    const fieldWithOptions = {
      ...field,
      options: getFieldOptions(field),
      componentProps: {
        ...field.componentProps,
        loading: loadingFields[field.name],
      },
    };

    // Handle responsive span values
    const spanValue = span || field.span || 24;
    const colProps: any = {
      style: field.style,
      key: field.name,
    };

    // If span is an object (responsive), spread it directly
    // Ant Design Col accepts xs, sm, md, lg, xl, xxl props
    if (typeof spanValue === 'object') {
      Object.assign(colProps, spanValue);
    } else {
      colProps.span = spanValue;
    }

    // Handle offset, push, pull - can also be responsive
    if (field.offset) {
      if (typeof field.offset === 'object') {
        // For responsive offset, we need to merge with existing breakpoint values
        Object.keys(field.offset).forEach((breakpoint) => {
          const value = (field.offset as any)[breakpoint];
          if (typeof colProps[breakpoint] === 'object') {
            colProps[breakpoint] = { ...colProps[breakpoint], offset: value };
          } else if (typeof colProps[breakpoint] === 'number') {
            // If it's just a number (span), convert to object with span and offset
            colProps[breakpoint] = { span: colProps[breakpoint], offset: value };
          } else {
            colProps[breakpoint] = { offset: value };
          }
        });
      } else {
        colProps.offset = field.offset;
      }
    }

    if (field.push) {
      if (typeof field.push === 'object') {
        Object.keys(field.push).forEach((breakpoint) => {
          const value = (field.push as any)[breakpoint];
          if (typeof colProps[breakpoint] === 'object') {
            colProps[breakpoint] = { ...colProps[breakpoint], push: value };
          } else if (typeof colProps[breakpoint] === 'number') {
            colProps[breakpoint] = { span: colProps[breakpoint], push: value };
          } else {
            colProps[breakpoint] = { push: value };
          }
        });
      } else {
        colProps.push = field.push;
      }
    }

    if (field.pull) {
      if (typeof field.pull === 'object') {
        Object.keys(field.pull).forEach((breakpoint) => {
          const value = (field.pull as any)[breakpoint];
          if (typeof colProps[breakpoint] === 'object') {
            colProps[breakpoint] = { ...colProps[breakpoint], pull: value };
          } else if (typeof colProps[breakpoint] === 'number') {
            colProps[breakpoint] = { span: colProps[breakpoint], pull: value };
          } else {
            colProps[breakpoint] = { pull: value };
          }
        });
      } else {
        colProps.pull = field.pull;
      }
    }

    return (
      <Col {...colProps}>
        <FormField
          field={fieldWithOptions}
          mode={mode}
          value={formValues[field.name]}
          onChange={(value) => {
            form.setFieldValue(field.name, value);
          }}
          disabled={loading}
          readonly={isReadonly}
          formValues={formValues}
        />
      </Col>
    );
  };

  // Render fields in a row
  const renderFields = (fields: FieldConfig[]) => {
    const columns = config.columns || 1;
    const spanSize = 24 / columns;

    return (
      <Row gutter={[16, 0]}>
        {fields.map((field) => renderField(field, spanSize))}
      </Row>
    );
  };

  // Render form content
  const renderFormContent = () => {
    if (config.sections) {
      return config.sections.map((section, index) => {
        if (section.collapsible) {
          return (
            <Collapse
              key={index}
              defaultActiveKey={section.defaultCollapsed ? [] : ['1']}
              className="dynamic-form-section-collapse"
            >
              <Panel header={section.title} key="1">
                {section.description && (
                  <p className="dynamic-form-section-description">
                    {section.description}
                  </p>
                )}
                {renderFields(section.fields)}
              </Panel>
            </Collapse>
          );
        }

        return (
          <div key={index} className="dynamic-form-section">
            {section.title && (
              <h3 className="dynamic-form-section-title">{section.title}</h3>
            )}
            {section.description && (
              <p className="dynamic-form-section-description">
                {section.description}
              </p>
            )}
            {renderFields(section.fields)}
          </div>
        );
      });
    }

    return renderFields(allFields);
  };

  return (
    <Form
      form={form}
      layout={config.layout || 'vertical'}
      onFinish={handleFinish}
      onFinishFailed={handleFinishFailed}
      onValuesChange={handleValuesChange}
      initialValues={initialValues}
      className={className}
      {...config.formProps}
    >
      {renderFormContent()}

      {mode !== 'view' && (config.showSubmit !== false || config.showReset) && (
        <Form.Item className="dynamic-form-actions">
          <Space>
            {config.showSubmit !== false && (
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                disabled={isReadonly}
              >
                {config.submitText || (mode === 'edit' ? 'Update' : 'Submit')}
              </Button>
            )}
            {config.showReset && (
              <Button
                onClick={() => {
                  form.resetFields();
                  resetDirtyState();
                }}
                disabled={loading || isReadonly}
              >
                {config.resetText || 'Reset'}
              </Button>
            )}
          </Space>
        </Form.Item>
      )}
    </Form>
  );
};

export default DynamicForm;
